/**
 * Loop breaker for the Oddle Pass account-verification hand-off.
 *
 * WHY: `redirectToCheckout` re-entered verification every single time
 * retrieve-customer-info answered `customer_require_verification`, with no
 * counter and no bail-out. On the redirect flow that call is a full
 * `window.location.href` navigation off the shop, so when the server-side
 * activation flag does not flip (e.g. `account_activated = 0`, as recorded for
 * the customer in OMS-185) the customer bounces shop → Oddle Pass → shop →
 * Oddle Pass forever. Nothing in the cycle could terminate it.
 *
 * Scope choices:
 * - sessionStorage, not localStorage: a genuinely stuck customer should get a
 *   clean slate in a new tab/session rather than be permanently blocked.
 * - keyed per storeOrderId: a different order is a different attempt, so a
 *   customer who hit the cap on one cart is not blocked on the next.
 * - the cap allows ONE real verification round trip. The first call is the
 *   legitimate flow; a second means the round trip did not clear the flag,
 *   which is the loop.
 */

const STORAGE_KEY = 'opVerificationAttempts'

export const MAX_VERIFICATION_ATTEMPTS = 1

type AttemptMap = Record<string, number>

const readAll = (): AttemptMap => {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY)
    if (!raw) return {}
    const parsed = JSON.parse(raw)
    return parsed && typeof parsed === 'object' ? (parsed as AttemptMap) : {}
  } catch {
    // Private-mode / quota / corrupt value. Fail open: never block checkout
    // because bookkeeping storage is unavailable.
    return {}
  }
}

const writeAll = (value: AttemptMap): void => {
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(value))
  } catch {
    // Fail open, as above.
  }
}

export const getVerificationAttempts = (storeOrderId?: string): number => {
  if (typeof window === 'undefined' || !storeOrderId) return 0
  return readAll()[storeOrderId] ?? 0
}

/** Records an attempt and returns the new count (1 for the first attempt). */
export const registerVerificationAttempt = (storeOrderId?: string): number => {
  if (typeof window === 'undefined' || !storeOrderId) return 0
  const all = readAll()
  const next = (all[storeOrderId] ?? 0) + 1
  writeAll({ ...all, [storeOrderId]: next })
  return next
}

/** Call once the customer is genuinely authenticated, so a later legitimate
 * verification prompt on the same order is not pre-blocked. */
export const clearVerificationAttempts = (storeOrderId?: string): void => {
  if (typeof window === 'undefined' || !storeOrderId) return
  const all = readAll()
  if (!(storeOrderId in all)) return
  delete all[storeOrderId]
  writeAll(all)
}

export const hasExhaustedVerificationAttempts = (
  storeOrderId?: string,
): boolean => getVerificationAttempts(storeOrderId) >= MAX_VERIFICATION_ATTEMPTS
